import { useDisclosure } from '@chakra-ui/react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBox,
  CmButton,
  CmFlex,
  CmHelpPopover,
  CmLongLoading,
  CmMessageCard,
  CmPopover,
  CmSelect,
} from '@/components/uiParts';
import { DeliveryServiceName } from '@/domain/models/DeliveryService';
import {
  CustomLinkedStoreItem,
  Group,
  LinkItemsDividedByDeliveryService,
} from '@/domain/models/LinkItem';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { sleep, unique } from '@/lib/util';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  useLinkedGroup,
  useUpdateLinkedGroup,
} from '@/services/LinkedGroup/hooks';
import { useLinkItem } from '@/services/LinkItem/hooks';

import { AccordionArea } from './components/AccordionArea';
import { AddedLinkitemArea } from './components/AddedLinkitemArea';
import { RegisterConfirmModal } from './components/RegisterConfirmModal';
import {
  ascSortAtStringValue,
  disabledResetButton,
  disabledSubmitButton,
  uniqueLinkedStoreItemId,
} from './function';

const OPERATION_AREA_ID = `operation-area`;
export const INPUT_GROUP_NAME_ID = `groupNameInput-`;
export const DIVIDED_CONTENT_WIDTH = '49%';
export const MAX_GROUP_NAME_LENGTH = 50;

type NewInputGroup = Util.PartiallyRequired<CustomGroup, 'validate'>;

type HandleGroup = {
  inputGroupName?: string;
  editGroupName?: string;
  isTentativeDelete?: boolean;
  isExpanded?: boolean;
  validate?: {
    groupName: {
      isInputed: boolean;
      isMax: boolean;
      isBlank: boolean;
    };
  };
};

export type CustomGroup = {
  linked_store_items: CustomLinkedStoreItem[];
  sort_index: number;
} & Group &
  HandleGroup;

const LinkItemsPage: React.FC = () => {
  const { managerAccountMe } = useManagerAccountMe();
  const history = useHistory();
  /* アクセス制御 */
  redirect404Page('setting_sales_state_group', history, managerAccountMe);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deliveryServices, setDeliveryServices] = useState<
    DeliveryServiceName[]
  >([]);
  const [targetGroupIndex, setTargetGroupIndex] = useState<number>(0);
  const [linkedGroups, setLinkedGroups] = useState<CustomGroup[]>([]);

  const uniqueLinkedStoreItemIdsByGroups = linkedGroups
    .map((group) =>
      group.linked_store_items.map((linkedStoreItem) =>
        linkedStoreItem.item_ids.map((item) =>
          uniqueLinkedStoreItemId(
            item.external_item_id,
            item.store_id,
            linkedStoreItem.item_name
          )
        )
      )
    )
    .flat(2);

  const [linkItems, setLinkItems] = useState<
    LinkItemsDividedByDeliveryService[]
  >([]);

  const initialLinkedGroup =
    LocalStorageHandler.get('initialLinkedGroup').groups ?? [];

  /****
   * アコーディオンでの処理メソッド
   ****/
  const operateAccordion = {
    isDisabledAddButton: (() => {
      if (targetGroupIndex === -1) return true; // マスクがセットされていない状態
      return (
        (linkedGroups[targetGroupIndex]?.isTentativeDelete ?? false) ||
        linkedGroups.length === 0
      );
    })(),

    deliveryServiceNames: linkItems.map((ds) => ds.delivery_service_name),
    allOpen: {
      value: 'allOpen' as const,
      execute: () => setDeliveryServices(operateAccordion.deliveryServiceNames),
    },
    allClose: {
      value: 'allClose' as const,
      execute: () => setDeliveryServices([]),
    },

    handleToggleAllAccordion: (e: ChangeEvent<HTMLSelectElement>) => {
      const targetEvent = e.target.value as keyof Pick<
        typeof operateAccordion,
        'allOpen' | 'allClose'
      >;
      operateAccordion[targetEvent].execute();
    },

    handleToggleAccordion: (deliveryService: DeliveryServiceName) => {
      if (deliveryServices.includes(deliveryService)) {
        const filterdDeliveryService = deliveryServices.filter(
          (ds) => ds !== deliveryService
        );
        setDeliveryServices(filterdDeliveryService);
      } else {
        setDeliveryServices([...deliveryServices, deliveryService]);
      }
    },

    handleAddItemToLinkedGroup: (linkedStoreItem: CustomLinkedStoreItem) => {
      const copyLinkedGroups = [...linkedGroups];
      copyLinkedGroups[targetGroupIndex].linked_store_items.push(
        linkedStoreItem
      );
      setLinkedGroups(copyLinkedGroups);
    },
  };

  const {
    isDisabledAddButton,
    handleToggleAllAccordion,
    handleToggleAccordion,
    handleAddItemToLinkedGroup,
  } = operateAccordion;

  /****
   * 紐付けグループの処理メソッド
   ****/
  const operateLinkedGroup = {
    checkGroupInputName: (value: string, groupIndex: number) => {
      const copyLinkedGroups = [...linkedGroups] as NewInputGroup[];
      copyLinkedGroups[groupIndex]['validate']['groupName']['isInputed'] = true;
      copyLinkedGroups[groupIndex]['validate']['groupName']['isMax'] =
        value.length > MAX_GROUP_NAME_LENGTH;
      copyLinkedGroups[groupIndex]['validate']['groupName']['isBlank'] =
        value === '';

      return copyLinkedGroups;
    },

    handleInputGroupName: (inputGroupName: string, groupIndex: number) => {
      const copyLinkedGroups = operateLinkedGroup.checkGroupInputName(
        inputGroupName,
        groupIndex
      );

      copyLinkedGroups[groupIndex]['inputGroupName'] = inputGroupName;
      setLinkedGroups(copyLinkedGroups);
    },

    handleAddEditGroupNameInput: (
      editGroupName: string,
      groupIndex: number
    ) => {
      const copyLinkedGroups = [...linkedGroups] as NewInputGroup[];

      const valudateProps = {
        groupName: {
          isInputed: true,
          isMax: false,
          isBlank: false,
        },
      };

      copyLinkedGroups[groupIndex]['validate'] = valudateProps;
      copyLinkedGroups[groupIndex]['editGroupName'] = editGroupName;

      setLinkedGroups(copyLinkedGroups);
      sleep(50).then(() => focusGroupNameInput(groupIndex));
    },

    handleEditGroupName: (editGroupName: string, groupIndex: number) => {
      const copyLinkedGroups = operateLinkedGroup.checkGroupInputName(
        editGroupName,
        groupIndex
      );

      copyLinkedGroups[groupIndex]['editGroupName'] = editGroupName;

      setLinkedGroups(copyLinkedGroups);
    },

    handleReturnEditGroupNameInput: (groupIndex: number) => {
      const copyLinkedGroups = [...linkedGroups] as CustomGroup[];

      delete copyLinkedGroups[groupIndex]['validate'];
      delete copyLinkedGroups[groupIndex]['editGroupName'];

      setLinkedGroups(copyLinkedGroups);
    },

    handleDeleteGroupItem: (groupIndex: number, itemIndex: number) => {
      const copyLinkedGroups = [...linkedGroups];
      copyLinkedGroups[groupIndex]['linked_store_items'].splice(itemIndex, 1);
      setLinkedGroups(copyLinkedGroups);
    },

    maskGroup: (groupIndex: number) => {
      const copyLinkedGroups = [...linkedGroups];
      copyLinkedGroups[groupIndex]['isTentativeDelete'] = true;
      copyLinkedGroups[groupIndex]['isExpanded'] = false;
      setLinkedGroups(copyLinkedGroups);
      handleReturnEditGroupNameInput(groupIndex);
    },

    deleteGroup: (groupIndex: number) => {
      const copyLinkedGroups = [...linkedGroups];
      copyLinkedGroups.splice(groupIndex, 1);
      setLinkedGroups(copyLinkedGroups);
    },

    handleRemoveMask: (groupIndex: number) => {
      const copyLinkedGroups = [...linkedGroups];
      copyLinkedGroups[groupIndex]['isTentativeDelete'] = false;
      setLinkedGroups(copyLinkedGroups);
    },

    handleDeleteGroup: (
      targetEvent: 'maskGroup' | 'deleteGroup',
      groupIndex: number
    ) => {
      if (targetGroupIndex === groupIndex) {
        handleChangeTargetGroupIndex(-1);
      }
      operateLinkedGroup[targetEvent](groupIndex);
    },

    focusGroupNameInput: (index = 0) => {
      const inputElement = document.getElementById(
        `${INPUT_GROUP_NAME_ID + index.toString()}`
      ) as HTMLElement;

      inputElement?.focus();
    },

    handleChangeTargetGroupIndex: (value: number) => setTargetGroupIndex(value),

    handleToggleGroupAccordion: (groupIndex: number) => {
      const copyLinkedGroups = [...linkedGroups];
      copyLinkedGroups[groupIndex]['isExpanded'] =
        !copyLinkedGroups[groupIndex]['isExpanded'];
      setLinkedGroups(copyLinkedGroups);
    },

    handleOnDragStart: () => {
      handleChangeTargetGroupIndex(-1);
    },

    handleOnDragEnd: (result: DropResult) => {
      const destinationIndex = result.destination?.index ?? 0;
      const sourceIndex = result.source.index;
      if (destinationIndex === sourceIndex) return;

      const newItems = Array.from(linkedGroups);
      const [reorderItem] = newItems.splice(sourceIndex, 1);
      newItems.splice(destinationIndex, 0, reorderItem);
      const sortLinkedGroups = newItems.map((item, index) => {
        item.sort_index = newItems.length - index;
        return item;
      });
      setLinkedGroups(sortLinkedGroups);
    },
  };

  const {
    handleInputGroupName,
    handleDeleteGroupItem,
    handleDeleteGroup,
    handleRemoveMask,
    handleChangeTargetGroupIndex,
    focusGroupNameInput,
    handleAddEditGroupNameInput,
    handleEditGroupName,
    handleReturnEditGroupNameInput,
    handleToggleGroupAccordion,
    handleOnDragStart,
    handleOnDragEnd,
  } = operateLinkedGroup;

  /****
   * ボタングループの処理メソッド
   ****/
  const operateButtons = {
    handleOpenPopover: () => setIsPopoverOpen(true),

    handleClosePopover: () => setIsPopoverOpen(false),

    isEqualLinkedItems: (() =>
      JSON.stringify(initialLinkedGroup) === JSON.stringify(linkedGroups))(),

    isInitStateLinkedGroups: (() =>
      linkedGroups.length === 1 && linkedGroups[0].inputGroupName === '')(),

    isDisabledResetButton: () => {
      return disabledResetButton(
        operateButtons.isEqualLinkedItems,
        operateButtons.isInitStateLinkedGroups
      );
    },

    isDisabledSubmitButton: () => {
      return disabledSubmitButton(
        linkedGroups,
        initialLinkedGroup,
        operateButtons.isDisabledResetButton()
      );
    },

    setGroups: (groups: CustomGroup[] = []) => {
      setLinkedGroups([
        {
          group_id: uuidv4(), // NOTE バックエンドから生成の希望があり、フロントで追加対応
          linked_store_items: [],
          inputGroupName: '',
          validate: {
            groupName: {
              isBlank: true,
              isMax: false,
              isInputed: false,
            },
          },
          sort_index: groups[0] ? groups[0].sort_index + 1 : 1,
        },
        ...groups,
      ]);
    },

    clearLinkedGroups: () => {
      operateButtons.setGroups();
      setTargetGroupIndex(0);
      sleep(50).then(() => focusGroupNameInput());
    },

    resetLinkedGroups: () => {
      if (initialLinkedGroup.length === 0) {
        operateButtons.clearLinkedGroups();
        return;
      }
      handleClosePopover();
      setTargetGroupIndex(0);
      setLinkedGroups(initialLinkedGroup);
    },

    handleAddGroup: () => {
      operateButtons.setGroups(linkedGroups);
      setTargetGroupIndex(0);
      sleep(50).then(() => focusGroupNameInput());
    },

    handleUpdateLinkedGroupItems: async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);
      const params = {
        groups: linkedGroups
          .filter((group) => !group.isTentativeDelete)
          .map((group) => {
            return {
              group_id: group.group_id,
              group_name:
                group.editGroupName ?? group.inputGroupName ?? group.group_name,
              linked_store_items: group.linked_store_items,
              sort_index: group.sort_index,
            };
          }),
      };

      await useUpdateLinkedGroup(params);
      window.alert('一括品切れ商品紐付けを更新しました。');
      await initialize.linkItems();
      await initialize.linkedGroups();
      setIsLoading(false);
      onClose();
    },
  };

  const {
    isDisabledResetButton,
    isDisabledSubmitButton,
    handleOpenPopover,
    handleClosePopover,
    handleAddGroup,
    resetLinkedGroups,
    clearLinkedGroups,
    handleUpdateLinkedGroupItems,
  } = operateButtons;

  /****
   * 初期化
   ****/
  const initialize = {
    linkedGroups: async () => {
      const reslinkedGroup = await useLinkedGroup();
      const linkedGroups: CustomGroup[] = reslinkedGroup.groups
        .map((group) => {
          return {
            ...group,
            isTentativeDelete: false,
          };
        })
        .sort((a, b) => b.sort_index - a.sort_index);

      /* NOTE 当初実装時にローカルストレージではなく、useStateに値をコピーして置いていたが、リセットの動作をしたときにコピーをした値を用いても、コピーをした値が参照渡し？によって値が書き換わってしま、うまく動作をしなかったという経緯でローカルストレージを使用 */
      LocalStorageHandler.set('initialLinkedGroup', {
        groups: linkedGroups,
      });
      if (linkedGroups.length === 0) {
        clearLinkedGroups();
      } else {
        setLinkedGroups(linkedGroups);
        setTargetGroupIndex(0);
        sleep(50).then(() => focusGroupNameInput());
      }
    },

    linkItems: async () => {
      const reslinkItems = await useLinkItem();

      const uniqueDeliveryServiceNames = unique(
        reslinkItems.map((item) => item.delivery_service_name)
      );

      const mapedDeliveryServiceItems = uniqueDeliveryServiceNames.map(
        (deliveryServiceName) => {
          return {
            delivery_service_name: deliveryServiceName,
          };
        }
      );

      const linkItemsDividedByDeliveryService = mapedDeliveryServiceItems.map(
        (item) => {
          return {
            delivery_service_name: item.delivery_service_name,
            linked_store_items: (() => {
              const filterdLinkedStoreItems = reslinkItems.filter(
                (fetchItem) => {
                  return (
                    fetchItem.delivery_service_name ===
                    item.delivery_service_name
                  );
                }
              );

              return ascSortAtStringValue(filterdLinkedStoreItems, 'item_name');
            })(),
          };
        }
      );

      setLinkItems(
        ascSortAtStringValue(
          linkItemsDividedByDeliveryService,
          'delivery_service_name'
        )
      );
    },
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await initialize.linkItems();
      await initialize.linkedGroups();
      setIsLoading(false);
    })();

    // ページを離れたら不要になるのでリセットさせる。
    return () => {
      LocalStorageHandler.removeItem('initialLinkedGroup');
    };
  }, []);

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, OPERATION_AREA_ID]);

  return (
    <PageLayout className="link-items" title={routes.LinkItems.title}>
      {linkItems.length > 0 ? (
        <div>
          <CmBox id={OPERATION_AREA_ID} padding={'15px 0'}>
            <CmFlex justifyContent="space-between" alignItems="flex-start">
              <CmBox width={DIVIDED_CONTENT_WIDTH}>
                <CmBox width="50%">
                  <CmSelect
                    id="select"
                    value="01"
                    onChange={handleToggleAllAccordion}
                  >
                    <option hidden value="01">
                      開閉状態を選択
                    </option>
                    <option value={operateAccordion.allOpen.value}>
                      アコーディオンを全て開く
                    </option>
                    <option value={operateAccordion.allClose.value}>
                      アコーディオンを全て閉じる
                    </option>
                  </CmSelect>
                </CmBox>
              </CmBox>
              <CmBox width={DIVIDED_CONTENT_WIDTH}>
                <CmFlex alignItems="center" flexDirection="row-reverse">
                  <CmPopover
                    title={'リセット確認'}
                    isOpen={isPopoverOpen}
                    placement="top-start"
                    onOpen={handleOpenPopover}
                    onClose={handleClosePopover}
                    handleOk={resetLinkedGroups}
                    button={
                      <CmButton
                        isDisabled={isDisabledResetButton()}
                        ml="3"
                        variant="outline"
                      >
                        リセット
                      </CmButton>
                    }
                  >
                    商品の紐付けを画面初期表示の状態へリセットします。
                  </CmPopover>

                  {/*
                   // @ts-ignore TS2590 */}
                  <CmButton
                    isDisabled={isDisabledSubmitButton()}
                    ml="10"
                    onClick={onOpen}
                  >
                    登録
                  </CmButton>
                  <CmBox ml="10" width="1px" height="25px" bg="black" />
                  <CmBox ml="4">
                    <CmHelpPopover>
                      1つの商品を複数のグループに紐づけることはできません。
                    </CmHelpPopover>
                  </CmBox>
                  <CmButton onClick={handleAddGroup}>グループ追加</CmButton>
                </CmFlex>
                <RegisterConfirmModal
                  {...{
                    isLoading,
                    isOpen,
                    linkedGroups,
                    handleUpdateLinkedGroupItems,
                    onClose,
                  }}
                />
              </CmBox>
            </CmFlex>
          </CmBox>

          <CmFlex justifyContent="space-between">
            <AccordionArea
              minusHeight={targetsHeight}
              {...{
                isDisabledAddButton,
                uniqueLinkedStoreItemIdsByGroups,
                linkItems,
                deliveryServices,
                handleToggleAccordion,
                handleAddItemToLinkedGroup,
              }}
            />
            {linkedGroups.length > 0 ? (
              <AddedLinkitemArea
                minusHeight={targetsHeight}
                {...{
                  linkedGroups,
                  targetGroupIndex,
                  handleChangeTargetGroupIndex,
                  handleInputGroupName,
                  handleDeleteGroupItem,
                  handleDeleteGroup,
                  handleRemoveMask,
                  handleAddEditGroupNameInput,
                  handleEditGroupName,
                  handleReturnEditGroupNameInput,
                  handleOnDragStart,
                  handleToggleGroupAccordion,
                  handleOnDragEnd,
                }}
              />
            ) : (
              !isLoading && (
                <CmBox width={DIVIDED_CONTENT_WIDTH}>
                  <CmMessageCard margin="100px auto 0">
                    グループ登録がありません。
                  </CmMessageCard>
                </CmBox>
              )
            )}
          </CmFlex>
        </div>
      ) : (
        <>
          {!isLoading && (
            <CmMessageCard margin="100px auto 0">
              商品登録がないため、紐付け登録ができません。
            </CmMessageCard>
          )}
        </>
      )}
      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default LinkItemsPage;
